import * as THREE from 'three';

type ScreenPoint = { x: number; y: number };
type DimensionElements = {
  group: SVGGElement;
  extensionLines: [SVGLineElement, SVGLineElement];
  dimensionLine: SVGLineElement;
  endpointDots: [SVGCircleElement, SVGCircleElement];
  label: SVGTextElement;
};

/** SVG architectural dimensions rendered over the 3D viewport. */
export class DimensionOverlay {
  public readonly el: SVGSVGElement;
  private readonly axes: DimensionElements[];

  constructor(container: HTMLElement) {
    container.style.position = 'relative';
    this.el = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.el.setAttribute('aria-hidden', 'true');
    this.el.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;overflow:visible;pointer-events:none;z-index:12;display:block;';
    container.appendChild(this.el);
    this.axes = [0, 1, 2].map(() => this.createAxis());
  }

  public setVisible(visible: boolean): void {
    this.el.style.display = visible ? 'block' : 'none';
  }

  public update(bounds: THREE.Box3, camera: THREE.Camera, width: number, height: number): void {
    if (this.el.style.display === 'none') return;
    if (bounds.isEmpty() || width <= 0 || height <= 0) {
      this.axes.forEach((axis) => { axis.group.style.display = 'none'; });
      return;
    }

    const size = new THREE.Vector3();
    bounds.getSize(size);
    const gap = Math.max(Math.max(size.x, size.y, size.z) * 0.075, 3);
    const { min, max } = bounds;
    const dimensions = [
      { value: size.x, from: new THREE.Vector3(min.x, min.y - gap, min.z), to: new THREE.Vector3(max.x, min.y - gap, min.z), extFrom: new THREE.Vector3(min.x, min.y, min.z), extTo: new THREE.Vector3(max.x, min.y, min.z) },
      { value: size.y, from: new THREE.Vector3(max.x + gap, min.y, min.z), to: new THREE.Vector3(max.x + gap, max.y, min.z), extFrom: new THREE.Vector3(max.x, min.y, min.z), extTo: new THREE.Vector3(max.x, max.y, min.z) },
      { value: size.z, from: new THREE.Vector3(max.x + gap, max.y + gap, min.z), to: new THREE.Vector3(max.x + gap, max.y + gap, max.z), extFrom: new THREE.Vector3(max.x, max.y, min.z), extTo: new THREE.Vector3(max.x, max.y, max.z) },
    ];
    camera.updateMatrixWorld();
    const project = (point: THREE.Vector3): ScreenPoint => {
      const projected = point.clone().project(camera);
      return { x: (projected.x * 0.5 + 0.5) * width, y: (-projected.y * 0.5 + 0.5) * height };
    };

    dimensions.forEach((dimension, index) => {
      const elements = this.axes[index]!;
      const a = project(dimension.from);
      const b = project(dimension.to);
      this.setLine(elements.extensionLines[0], project(dimension.extFrom), a);
      this.setLine(elements.extensionLines[1], project(dimension.extTo), b);
      this.setLine(elements.dimensionLine, a, b);
      [a, b].forEach((point, dotIndex) => {
        elements.endpointDots[dotIndex]!.setAttribute('cx', point.x.toFixed(1));
        elements.endpointDots[dotIndex]!.setAttribute('cy', point.y.toFixed(1));
      });
      elements.label.setAttribute('x', ((a.x + b.x) / 2).toFixed(1));
      elements.label.setAttribute('y', ((a.y + b.y) / 2 - 9).toFixed(1));
      elements.label.textContent = dimension.value.toFixed(1);
      elements.group.style.display = 'block';
    });
  }

  public dispose(): void {
    this.el.remove();
  }

  private createAxis(): DimensionElements {
    const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    const extensionLines = [0, 1].map(() => {
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('stroke', '#475569');
      line.setAttribute('stroke-width', '1');
      line.setAttribute('stroke-opacity', '0.65');
      group.appendChild(line);
      return line;
    }) as [SVGLineElement, SVGLineElement];
    const dimensionLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    dimensionLine.setAttribute('stroke', '#1e293b');
    dimensionLine.setAttribute('stroke-width', '1.5');
    group.appendChild(dimensionLine);
    const endpointDots = [0, 1].map(() => {
      const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      dot.setAttribute('r', '3');
      dot.setAttribute('fill', '#2563eb');
      dot.setAttribute('stroke', '#fff');
      dot.setAttribute('stroke-width', '1');
      group.appendChild(dot);
      return dot;
    }) as [SVGCircleElement, SVGCircleElement];
    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    label.setAttribute('text-anchor', 'middle');
    label.setAttribute('dominant-baseline', 'central');
    label.setAttribute('font-family', 'Arial, sans-serif');
    label.setAttribute('font-size', '12');
    label.setAttribute('font-weight', '700');
    label.setAttribute('fill', '#0f172a');
    label.setAttribute('stroke', '#fff');
    label.setAttribute('stroke-width', '4');
    label.setAttribute('paint-order', 'stroke');
    group.appendChild(label);
    this.el.appendChild(group);
    return { group, extensionLines, dimensionLine, endpointDots, label };
  }

  private setLine(line: SVGLineElement, from: ScreenPoint, to: ScreenPoint): void {
    line.setAttribute('x1', from.x.toFixed(1));
    line.setAttribute('y1', from.y.toFixed(1));
    line.setAttribute('x2', to.x.toFixed(1));
    line.setAttribute('y2', to.y.toFixed(1));
  }
}
